/**
 * utils/importCommit.js
 * Turns catalog-import output (importParser.js's parseWorkbook result) plus the user's
 * reviewed decisions into final app-shaped data: a category-prefix plan, market-code
 * split/kept overrides applied, and a fully-minted commit ready for a single `update()` call.
 * No UI, no storage writes — the import wizard (3b-ii) owns both of those.
 *
 * Pure throughout: no I/O, no mutation of any input (existingData especially — items,
 * categories, cars, history are all read-only here), deterministic given an explicit date.
 */
import { suggestCategoryCode, validateCategoryCode } from "./categoryCode";
import { maxCodeNumbersByPrefix } from "./idGenerators";
import { creationEntries } from "./history";

const norm = (s) => String(s || "").trim().toUpperCase();

/**
 * Build a category-prefix plan for every parsed category: reuse an existing category's code
 * (permanent, read-only in the UI) when the name already exists (trim+case-insensitive
 * match), else suggest a new one via suggestCategoryCode. Suggestions stay unique across the
 * WHOLE plan by folding earlier-assigned codes into the "already in use" set as we go.
 *
 * @param {{categories: {name: string}[]}} parsed
 * @param {{categories: {name: string, code: string}[]}} existingData
 * @returns {{name: string, code: string, suggested: boolean, existing: boolean}[]}
 */
export function buildPrefixPlan(parsed, existingData) {
  const existingCategories = existingData?.categories || [];
  const allCodesSoFar = existingCategories.map((c) => c.code);

  return (parsed?.categories || []).map((cat) => {
    const name = cat.name;
    const match = existingCategories.find((c) => norm(c.name) === norm(name));
    if (match) {
      return { name, code: match.code, suggested: false, existing: true };
    }
    const code = suggestCategoryCode(name, allCodesSoFar);
    allCodesSoFar.push(code);
    return { name, code, suggested: true, existing: false };
  });
}

/**
 * Re-validate every non-existing (user-editable) plan row's code — catches a user edit that
 * collides with either an existing category's code or another row's code (buildPrefixPlan
 * only guarantees uniqueness at suggestion time; the UI lets the user hand-edit a suggested
 * code afterward, which can reintroduce a collision).
 *
 * @param {{name: string, code: string, existing: boolean}[]} plan
 * @param {{categories: {code: string}[]}} existingData
 * @returns {{ok: boolean, errors: {name: string, error: string}[]}}
 */
export function validatePrefixPlan(plan, existingData) {
  const existingCodes = (existingData?.categories || []).map((c) => c.code);
  const errors = [];
  plan.forEach((row, i) => {
    if (row.existing) return; // permanent code, not user-editable, nothing to validate
    const otherCodes = [
      ...existingCodes,
      ...plan.filter((_, j) => j !== i).map((r) => r.code),
    ];
    const error = validateCategoryCode(row.code, otherCodes);
    if (error) errors.push({ name: row.name, error });
  });
  return { ok: errors.length === 0, errors };
}

/**
 * Split/kept code computation for one review-list entry — the SINGLE source of truth for this
 * logic (task B2a): both applyReviewDecisions (below, the real commit) and ImportWizard.js's
 * step-4 live preview call this, so the two can never diverge.
 *
 * @param {string} raw
 * @param {"split"|"kept"} decision
 * @param {string[]} [customCodes] — task B2b-i: user-hand-edited codes, when present. Cleaned
 *   (trimmed, empties dropped) and returned as-is, REGARDLESS of `decision` — the caller
 *   decides when it's appropriate to pass this (B2b-ii only will, for split rows). An empty
 *   array/null/undefined is treated as "no custom codes" and falls through to the normal
 *   split/kept computation below — unchanged by construction from pre-B2b-i behavior. If
 *   cleaning a NON-empty `customCodes` yields zero surviving codes (e.g. all-whitespace
 *   entries), this ALSO falls through rather than ever returning zero codes for a non-empty
 *   input — a hand-edit that accidentally clears itself shouldn't silently orphan the item.
 * @returns {string[]}
 */
export function codesForEntry(raw, decision, customCodes) {
  if (customCodes && customCodes.length > 0) {
    const cleaned = customCodes.map((c) => String(c).trim()).filter((c) => c !== "");
    if (cleaned.length > 0) return cleaned;
  }
  if (decision === "split") {
    return raw.split("/").map((p) => p.trim()).filter((p) => p !== "");
  }
  return [raw.trim()];
}

/**
 * Apply the user's split/kept overrides from the review pass onto the parsed items.
 * overrides: { [reviewIndex]: OverrideEntry } — only rows the user flipped from the parser's
 * own decision; everything else (untouched review rows, and items that were never ambiguous in
 * the first place) passes through with the SAME object reference, unchanged.
 *
 * OverrideEntry (task B2b-i) is EITHER shape:
 *   - a bare "split"|"kept" string (the original, pre-B2b-i shape — every existing caller keeps
 *     working identically, unchanged)
 *   - an object `{ decision: "split"|"kept", codes?: string[] }` — `codes`, when present, is
 *     passed through to codesForEntry as its user-edited customCodes.
 * An object with no `decision` is treated as "not flipped" (same as a falsy bare override) and
 * skipped — a malformed override can never silently mint wrong codes. No "reviewed" flag lives
 * here: whether the user has looked at a row is UI state (ImportWizard.js, task B2b-ii); this
 * layer only ever sees what actually affects the committed data.
 *
 * @param {object[]} parsedItems
 * @param {{raw: string, itemIndex: number}[]} reviewList
 * @param {Object<number, "split"|"kept"|{decision:"split"|"kept", codes?:string[]}>} [overrides]
 * @returns {object[]}
 */
export function applyReviewDecisions(parsedItems, reviewList, overrides) {
  if (!overrides || Object.keys(overrides).length === 0) return parsedItems;

  const items = [...parsedItems];
  reviewList.forEach((entry, i) => {
    const raw = overrides[i];
    const decision = typeof raw === "object" && raw !== null ? raw.decision : raw;
    if (!decision) return; // not flipped by the user (or a malformed object override)
    const customCodes = typeof raw === "object" && raw !== null ? raw.codes : undefined;
    const itemIndex = entry.itemIndex;
    if (itemIndex == null || itemIndex < 0 || itemIndex >= items.length) return; // bogus link, safe no-op
    const codes = codesForEntry(entry.raw, decision, customCodes);
    items[itemIndex] = { ...items[itemIndex], marketCodes: codes };
  });
  return items;
}

/**
 * Build the final commit: every parsed item minted with a real internalCode, categories/cars
 * merged with existingData, and creationEntries for the whole batch under one shared date.
 *
 * Minting is O(items) total, NOT O(items * existingItems) and NOT O(distinct prefixes * total
 * data size): maxCodeNumbersByPrefix (idGenerators.js) scans existingData ONCE, up front,
 * covering every prefix simultaneously — items, transaction lines, history entries, recipe
 * lines, and transformation lines all in one pass each, regardless of how many items or how
 * many distinct category prefixes are in this batch (item-code-reuse fix). Every item's code is
 * then a plain O(1) map lookup + local increment, formatted byte-identical to
 * nextInternalCode's own `${prefix}-${String(n).padStart(5,"0")}` format. The OLD approach
 * (calling nextInternalCode, itself now a full-data scan, once per distinct prefix) would have
 * been O(distinct prefixes * total data size) — a real regression this rewrite specifically
 * avoids, not just an equivalent restatement of the old per-item-array-only shape.
 *
 * SAFETY NOTE: seeding trusts every plan prefix to be `[A-Z0-9]+` (so
 * `${prefix}-${digits}` round-trips cleanly through maxCodeNumbersByPrefix's own
 * `^([A-Z0-9]+)-(\d+)$` RegExp with no escaping concerns) — that invariant is enforced by
 * validateCategoryCode, not here. Callers MUST run validatePrefixPlan (ok===true) before
 * calling buildCommit; this function does not re-validate prefixes itself.
 *
 * @param {{items: object[], categories: {name:string}[], cars: {name:string}[]}} parsed
 * @param {{name: string, code: string, existing: boolean}[]} prefixPlan
 * @param {{items: object[], categories: object[], cars: object[], history: object[], transactions: object[], recipes: object[], transformations: object[]}} existingData
 * @param {{date?: string}} [options]
 * @returns {{items: object[], categories: object[], cars: object[], history: object[], stats: object}}
 */
export function buildCommit(parsed, prefixPlan, existingData, { date } = {}) {
  const commitDate = date || new Date().toISOString();
  const existingCategories = existingData?.categories || [];
  const existingCars = existingData?.cars || [];
  const existingHistory = existingData?.history || [];

  // Categories: existing (untouched) + the plan's new ones, plan order preserved.
  const newCategoryRows = prefixPlan.filter((row) => !row.existing);
  const categories = [
    ...existingCategories,
    ...newCategoryRows.map((row) => ({ name: row.name, code: row.code })),
  ];

  const prefixByCategory = new Map(prefixPlan.map((row) => [norm(row.name), row.code]));

  // ONE full scan of existingData (items + transaction lines + history + recipe lines +
  // transformation lines) for the WHOLE batch — never re-scanned per item or per prefix below.
  const maxByPrefix = maxCodeNumbersByPrefix(existingData || {});
  const counters = new Map(); // prefix -> next number to mint
  const items = (parsed.items || []).map((it) => {
    const prefix = prefixByCategory.get(norm(it.category));
    if (!counters.has(prefix)) {
      counters.set(prefix, (maxByPrefix.get(prefix) || 0) + 1);
    }
    const n = counters.get(prefix);
    counters.set(prefix, n + 1);
    return { ...it, internalCode: `${prefix}-${String(n).padStart(5, "0")}` };
  });
  const itemsMinted = items.length;

  // Cars: existing + parsed cars not already present (case-insensitive), order preserved.
  const seenCarNames = new Set(existingCars.map((c) => norm(c.name)));
  const newCars = [];
  for (const c of parsed.cars || []) {
    const key = norm(c.name);
    if (seenCarNames.has(key)) continue;
    seenCarNames.add(key);
    newCars.push({ name: c.name });
  }
  const cars = [...existingCars, ...newCars];

  // History: creationEntries per newly-minted item, one shared date, appended after existing.
  const newHistory = items.flatMap((it) => creationEntries(it, commitDate));
  const history = [...existingHistory, ...newHistory];

  const stats = {
    itemsMinted,
    categoriesCreated: newCategoryRows.length,
    categoriesReused: prefixPlan.length - newCategoryRows.length,
    carsCreated: newCars.length,
    historyEntries: newHistory.length,
  };

  return { items, categories, cars, history, stats };
}
